import { FieldType, FieldVisibility } from "./forms.js";
import { Carrier } from "./carriers.js";
export const CARRIER_CHANGE_REQUEST_TYPE_LABELS = {
    HIERARCHY_CHANGE: "Hierarchy Change",
};
export const CARRIER_CHANGE_REQUEST_STATUS_LABELS = {
    DRAFT: "Draft",
    SUBMITTED: "Submitted to Carrier",
    EMAIL_RECEIVED: "Carrier Receipt Confirmed",
    FOLLOW_UP_DUE: "Follow-up Due",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
};
export const TRANSAMERICA_CHANGE_REQUEST_EMAIL = "TLP-CRcontractadmin@transamerica.com";
export const TRANSAMERICA_FOLLOW_UP_PHONE = "1-866-834-0436";
/** Default business days for hierarchy changes when carrier auto-reply does not specify. */
export const TRANSAMERICA_HIERARCHY_CHANGE_BUSINESS_DAYS = 3;
export const TRANSAMERICA_SUPPORTED_CARRIERS = [Carrier.TRANSAMERICA];
const writingNumberField = (id, label) => ({
    id,
    name: id,
    label,
    type: FieldType.TEXT,
    required: true,
    placeholder: "Writing number",
    visibility: FieldVisibility.ALWAYS,
});
const nameField = (id, label) => ({
    id,
    name: id,
    label,
    type: FieldType.TEXT,
    required: true,
    placeholder: "Full name",
    visibility: FieldVisibility.ALWAYS,
});
export const CARRIER_CHANGE_REQUEST_SECTIONS = {
    HIERARCHY_CHANGE: {
        id: "hierarchy_change",
        title: "Transamerica Hierarchy Change",
        description: "Submit a hierarchy change to Transamerica contract administration. An email will be sent to Transamerica on your behalf.",
        fields: [
            nameField("agentName", "Agent Name"),
            writingNumberField("agentWritingNumber", "Agent Writing Number"),
            nameField("currentUplineName", "Current Upline Name"),
            writingNumberField("currentUplineWritingNumber", "Current Upline Writing Number"),
            nameField("newUplineName", "New Upline Name"),
            writingNumberField("newUplineWritingNumber", "New Upline Writing Number"),
            {
                id: "reason",
                name: "reason",
                label: "Internal Notes (optional)",
                type: FieldType.TEXTAREA,
                required: false,
                placeholder: "Notes for your team — not sent to Transamerica unless included in the email preview.",
                visibility: FieldVisibility.ALWAYS,
            },
        ],
    },
};
export function buildTransamericaHierarchyChangeSubject(formData) {
    return `Hierarchy Change Request: ${formData.agentName} (${formData.agentWritingNumber})`;
}
export function buildTransamericaHierarchyChangeBody(formData) {
    const lines = [
        "Please process the following hierarchy change request.",
        "",
        `Agent: ${formData.agentName} (${formData.agentWritingNumber})`,
        "",
        "Current hierarchy:",
        `  Upline: ${formData.currentUplineName} (${formData.currentUplineWritingNumber})`,
        "",
        "Requested hierarchy:",
        `  New upline: ${formData.newUplineName} (${formData.newUplineWritingNumber})`,
        "",
        "Thank you.",
    ];
    return lines.join("\n");
}
export function addBusinessDays(start, businessDays) {
    const result = new Date(start);
    let added = 0;
    while (added < businessDays) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) {
            added += 1;
        }
    }
    return result;
}
/** Parse Transamerica auto-receipt entitled subject, e.g. "Hierarchy Change Request: MLSR204169". */
export function parseTransamericaReceiptEntitledSubject(content) {
    const match = content.match(/entitled\s+['"]([^'"]+)['"]/i);
    return match?.[1]?.trim() ?? null;
}
/** Extract a reference token from an entitled subject for fuzzy matching. */
export function extractTransamericaReference(entitledSubject) {
    const colonMatch = entitledSubject.match(/:\s*(.+)$/);
    return colonMatch?.[1]?.trim() ?? null;
}
/**
 * Parse business-day SLA from Transamerica receipt body.
 * Returns the upper bound when a range is given (e.g. "2-3 business days" → 3).
 */
export function parseTransamericaBusinessDaysFromReceipt(content) {
    const normalized = content.replace(/\s+/g, " ");
    const patterns = [
        { regex: /appointment requests:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i, type: "GENERAL" },
        { regex: /contact information updates:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i, type: "GENERAL" },
        { regex: /direct deposit updates:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i, type: "GENERAL" },
        { regex: /general inquiries:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i, type: "HIERARCHY_CHANGE" },
        { regex: /termination requests:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i, type: "GENERAL" },
        {
            regex: /agent changes on inforce policies:\s*(\d+)(?:\s*-\s*(\d+))?\s*business days/i,
            type: "GENERAL",
        },
    ];
    for (const { regex, type } of patterns) {
        const match = normalized.match(regex);
        if (!match)
            continue;
        const upper = match[2] ? Number.parseInt(match[2], 10) : Number.parseInt(match[1], 10);
        if (Number.isFinite(upper)) {
            return type === "HIERARCHY_CHANGE" ? upper : upper;
        }
    }
    return null;
}
export function isTransamericaInboundEmail(from, subject, body) {
    const fromLower = (from ?? "").toLowerCase();
    const subjectLower = (subject ?? "").toLowerCase();
    const bodyLower = body.toLowerCase();
    const fromTransamerica = fromLower.includes("transamerica.com");
    const looksLikeReceipt = subjectLower.includes("email received") ||
        bodyLower.includes("confirm receipt of your email") ||
        bodyLower.includes("thank you for contacting transamerica");
    return fromTransamerica && looksLikeReceipt;
}
